import { WeatherData, MarineData, Coordinate } from '../types';

export class WeatherService {
  private static readonly LAT = 24.1;
  private static readonly LON = 120.1;
  private static readonly TIMEZONE = 'Asia/Taipei';

  private weatherData: WeatherData | null = null;
  private marineData: MarineData | null = null;
  private listeners: Array<(weather: WeatherData | null, marine: MarineData | null) => void> = [];

  constructor() {}

  public addListener(fn: (weather: WeatherData | null, marine: MarineData | null) => void) {
    this.listeners.push(fn);
  }

  public getWeatherData(): WeatherData | null {
    return this.weatherData;
  }

  public getMarineData(): MarineData | null {
    return this.marineData;
  }

  public async fetchAll(): Promise<{ weather: WeatherData; marine: MarineData }> {
    const requestedCoord: Coordinate = { latitude: WeatherService.LAT, longitude: WeatherService.LON };

    // Set initial loading state
    this.weatherData = {
      requestedCoord,
      returnedCoord: requestedCoord,
      time: '載入中...',
      timezone: WeatherService.TIMEZONE,
      windSpeed10m: 0,
      windDirection10m: 0,
      status: 'loading',
      fetchedAt: new Date().toLocaleTimeString('zh-TW', { hour12: false })
    };

    this.marineData = {
      requestedCoord,
      returnedCoord: requestedCoord,
      time: '載入中...',
      timezone: WeatherService.TIMEZONE,
      waveHeight: 0,
      wavePeriod: 0,
      waveDirection: 0,
      status: 'loading',
      fetchedAt: new Date().toLocaleTimeString('zh-TW', { hour12: false })
    };

    this.notify();

    const weatherUrl = `https://api.open-meteo.com/v1/forecast?latitude=${WeatherService.LAT}&longitude=${WeatherService.LON}&current=wind_speed_10m,wind_direction_10m&wind_speed_unit=ms&timezone=${encodeURIComponent(WeatherService.TIMEZONE)}`;
    const marineUrl = `https://marine-api.open-meteo.com/v1/marine?latitude=${WeatherService.LAT}&longitude=${WeatherService.LON}&current=wave_height,wave_direction,wave_period&timezone=${encodeURIComponent(WeatherService.TIMEZONE)}`;

    const [weatherRes, marineRes] = await Promise.allSettled([
      fetch(weatherUrl).then(res => {
        if (!res.ok) throw new Error(`HTTP ${res.status}: ${res.statusText}`);
        return res.json();
      }),
      fetch(marineUrl).then(res => {
        if (!res.ok) throw new Error(`HTTP ${res.status}: ${res.statusText}`);
        return res.json();
      })
    ]);

    const nowStr = new Date().toLocaleTimeString('zh-TW', { hour12: false });

    // Handle Weather
    if (weatherRes.status === 'fulfilled') {
      const data = weatherRes.value;
      if (data.current && typeof data.current.wind_speed_10m === 'number') {
        this.weatherData = {
          requestedCoord,
          returnedCoord: {
            latitude: Number(data.latitude.toFixed(4)),
            longitude: Number(data.longitude.toFixed(4))
          },
          time: data.current.time,
          timezone: data.timezone || WeatherService.TIMEZONE,
          windSpeed10m: data.current.wind_speed_10m,
          windDirection10m: data.current.wind_direction_10m ?? 0,
          status: 'success',
          fetchedAt: nowStr
        };
      } else {
        this.weatherData = {
          requestedCoord,
          returnedCoord: requestedCoord,
          time: '無資料',
          timezone: WeatherService.TIMEZONE,
          windSpeed10m: 0,
          windDirection10m: 0,
          status: 'error',
          errorMessage: '回傳資料缺少 wind_speed_10m 欄位',
          fetchedAt: nowStr
        };
      }
    } else {
      this.weatherData = {
        requestedCoord,
        returnedCoord: requestedCoord,
        time: '連線失敗',
        timezone: WeatherService.TIMEZONE,
        windSpeed10m: 0,
        windDirection10m: 0,
        status: 'error',
        errorMessage: weatherRes.reason?.message || '天氣 API 請求失敗',
        fetchedAt: nowStr
      };
    }

    // Handle Marine
    if (marineRes.status === 'fulfilled') {
      const data = marineRes.value;
      if (data.current && (data.current.wave_height !== null && data.current.wave_height !== undefined)) {
        this.marineData = {
          requestedCoord,
          returnedCoord: {
            latitude: Number(data.latitude.toFixed(4)),
            longitude: Number(data.longitude.toFixed(4))
          },
          time: data.current.time,
          timezone: data.timezone || WeatherService.TIMEZONE,
          waveHeight: Number(data.current.wave_height.toFixed(2)),
          wavePeriod: Number((data.current.wave_period ?? 5).toFixed(1)),
          waveDirection: Math.round(data.current.wave_direction ?? 0),
          status: 'success',
          fetchedAt: nowStr
        };
      } else {
        this.marineData = {
          requestedCoord,
          returnedCoord: requestedCoord,
          time: '無資料',
          timezone: WeatherService.TIMEZONE,
          waveHeight: 0,
          wavePeriod: 0,
          waveDirection: 0,
          status: 'error',
          errorMessage: '回傳資料為 null（該網格當前可能無波浪數值）',
          fetchedAt: nowStr
        };
      }
    } else {
      this.marineData = {
        requestedCoord,
        returnedCoord: requestedCoord,
        time: '連線失敗',
        timezone: WeatherService.TIMEZONE,
        waveHeight: 0,
        wavePeriod: 0,
        waveDirection: 0,
        status: 'error',
        errorMessage: marineRes.reason?.message || '海況 API 請求失敗',
        fetchedAt: nowStr
      };
    }

    this.notify();
    return { weather: this.weatherData, marine: this.marineData };
  }

  private notify() {
    for (const fn of this.listeners) {
      fn(this.weatherData, this.marineData);
    }
  }
}
